import React, { MouseEventHandler, useState } from "react";
import { BeatLoader } from "react-spinners";
import { OrganizationResponseDto, Profile } from "../common/ObjectTypes";
import SideMenu from "../common/SideMenu";
import TextInput from "../common/TextInput";
import tailwindClassnames from "../common/TailwindClassnames";

import { SortedDown, SortedUp, SortingArrows } from "../common/ProjectIcons";
import { useTranslation } from "react-i18next";
import Pagination from "../common/Pagination";

type UsersPageProps = {
  users: Profile[];
  take: number;
  offset: number;
  totalCount: number;
  onPageClicked: Function;
  organizations: OrganizationResponseDto[];
  userSearchQuery: any;
  onChangeSearchQuery: Function;
  onSearch: any;
  onSelectUser: Function;
  onClickAdd: MouseEventHandler;
  requestSort: Function;
  loading: boolean;
  resultsLoaded: boolean;
};

const UsersList = ({
  users,
  take,
  offset,
  totalCount,
  onPageClicked,
  organizations,
  userSearchQuery,
  onChangeSearchQuery,
  onSearch,
  onSelectUser,
  onClickAdd,
  requestSort,
  loading,
  resultsLoaded,
}: UsersPageProps) => {
  const [sortDirection, setSortDirection] = useState(true);
  const [sortByColumn, setSortByColumn] = useState("lastname");
  const { t } = useTranslation();

  // Status column is only rendered while inactive accounts are included in
  // the results ("Show Inactive Accounts" checked -> is_active === false).
  const showStatusColumn = userSearchQuery.is_active === false;
  const columnCount = showStatusColumn ? 4 : 3;

  /**
   * Header sort control: renders the column label with the appropriate sort
   * indicator and requests a re-sort on click. `column` is the API sort key;
   * `columnKey` is the local sortByColumn state value.
   */
  const sortableHeader = (label: string, column: string, columnKey: string) => (
    <button
      type="button"
      className="inline-flex items-center gap-1 uppercase tracking-wider"
      onClick={() => {
        requestSort(column, sortDirection);
        setSortDirection(!sortDirection);
        setSortByColumn(columnKey);
      }}
    >
      {label}
      {sortByColumn === columnKey ? (
        sortDirection ? (
          <SortedUp />
        ) : (
          <SortedDown />
        )
      ) : (
        <SortingArrows />
      )}
    </button>
  );

  return (
    <div className="flex flex-row w-full">
      <SideMenu />
      <div className="overflow-x-auto w-full mx-8 mt-8">
        <div className={tailwindClassnames.pageHeaderRow}>
          <div>
            <h1 className={tailwindClassnames.pageTitle}>{t("Users")}</h1>
            <p className={tailwindClassnames.pageSubtitle}>
              {t("Manage who has access across your organizations")}
            </p>
          </div>
        </div>

        <div className={tailwindClassnames.tableCard}>
          <div className={tailwindClassnames.tableCardToolbar}>
            <TextInput
              name={"first_name"}
              label={t("First Name")}
              placeholder={t("Search first name")}
              value={userSearchQuery.first_name}
              error={""}
              disabled={false}
              className={tailwindClassnames.tableFilterInput}
              type={"text"}
              initialFocus={true}
              onChange={(e: any) => onChangeSearchQuery(e)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  onSearch();
                }
              }}
            />
            <TextInput
              name={"last_name"}
              label={t("Last Name")}
              placeholder={t("Search last name")}
              value={userSearchQuery.last_name}
              error={""}
              disabled={false}
              className={tailwindClassnames.tableFilterInput}
              type={"text"}
              initialFocus={false}
              onChange={(e: any) => onChangeSearchQuery(e)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  onSearch();
                }
              }}
            />
            <TextInput
              name={"email"}
              label={t("Email")}
              placeholder={t("Search email")}
              value={userSearchQuery.email}
              error={""}
              disabled={false}
              className={tailwindClassnames.tableFilterInput}
              type={"text"}
              initialFocus={false}
              onChange={(e: any) => onChangeSearchQuery(e)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  onSearch();
                }
              }}
            />
            <TextInput
              name={"organization"}
              label={t("Organization")}
              placeholder={t("Search organization")}
              value={userSearchQuery.organization}
              error={""}
              disabled={false}
              className={tailwindClassnames.tableFilterInput}
              type={"text"}
              initialFocus={false}
              onChange={(e: any) => onChangeSearchQuery(e)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  onSearch();
                }
              }}
            />
            <div className="flex flex-row items-center pb-2">
              <input
                className={tailwindClassnames.checkBox}
                checked={!userSearchQuery.is_active}
                type="checkbox"
                value="is_active"
                id="search_inactive"
                onChange={(e: any) => onChangeSearchQuery(e)}
              />
              <label
                className={tailwindClassnames.checkBoxLabel}
                htmlFor="search_inactive"
              >
                {t("Show Inactive Accounts")}
              </label>
            </div>
            <button
              className={tailwindClassnames.btnFilterApply}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                onSearch(event)
              }
            >
              {t("Apply filters")}
            </button>
            <div className="flex-1" />
            <button
              className={tailwindClassnames.btnPrimary}
              onClick={onClickAdd}
            >
              <span aria-hidden="true">+ </span>
              {t("Add user")}
            </button>
          </div>

          {loading ? (
            <div className="sweet-loading flex justify-center w-full py-8">
              <BeatLoader
                color={tailwindClassnames.skyBlueDark}
                loading={true}
                size={15}
                margin={5}
              />
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead>
                  <tr>
                    <th className={tailwindClassnames.tableCardHeadCell}>
                      {sortableHeader(t("Name"), "last_name", "lastname")}
                    </th>
                    <th className={tailwindClassnames.tableCardHeadCell}>
                      {sortableHeader(t("Email"), "email", "email")}
                    </th>
                    <th className={tailwindClassnames.tableCardHeadCell}>
                      {sortableHeader(
                        t("Organization"),
                        "organization",
                        "organization",
                      )}
                    </th>
                    {showStatusColumn && (
                      <th className={tailwindClassnames.tableCardHeadCell}>
                        {t("Status")}
                      </th>
                    )}
                  </tr>
                </thead>
                {resultsLoaded ? (
                  <tbody>
                    {users.length === 0 && (
                      <tr>
                        <td
                          colSpan={columnCount}
                          className="text-center py-3 text-primary-off-black dark:text-primary-off-white transition duration-300"
                        >
                          {t(
                            "No results! Try modifying your search filters or adding your first user.",
                          )}
                        </td>
                      </tr>
                    )}
                    {users.map((user, index) => (
                      <tr
                        key={index}
                        className={tailwindClassnames.tableCardRow}
                        onClick={() => onSelectUser(user.user_id)}
                      >
                        <td
                          className={tailwindClassnames.tableCardCellPrimary}
                          title={`${user.first_name ?? ""} ${
                            user.last_name ?? ""
                          }`.trim()}
                        >
                          {`${user.first_name ?? ""} ${
                            user.last_name ?? ""
                          }`.trim()}
                        </td>
                        <td
                          className={tailwindClassnames.tableCardCell}
                          title={user.email || undefined}
                        >
                          {user.email}
                        </td>
                        <td
                          className={tailwindClassnames.tableCardCell}
                          title={user.organization || undefined}
                        >
                          {user.organization}
                        </td>
                        {showStatusColumn && (
                          <td className={tailwindClassnames.tableCardCell}>
                            <span
                              className={
                                user.is_active
                                  ? tailwindClassnames.chipActive
                                  : tailwindClassnames.chipInactive
                              }
                            >
                              {user.is_active ? t("Active") : t("Inactive")}
                            </span>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td
                        colSpan={columnCount}
                        className="py-3 px-4 text-left text-sm text-primary-off-black dark:text-primary-off-white transition duration-300"
                      >
                        {t("Loading...")}
                      </td>
                    </tr>
                  </tbody>
                )}
              </table>
              <Pagination
                showText={true}
                handlePageClick={onPageClicked}
                pageCount={Math.ceil(totalCount / take)}
                resultStart={offset + 1}
                initialPage={Math.floor(offset / take)}
                resultEnd={
                  offset + take < totalCount ? offset + take : totalCount
                }
                totalCount={totalCount}
                take={take}
                loading={false}
                mainclassname={tailwindClassnames.tableCardFooter}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersList;
